import React, { useEffect, useState, useRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import { vibrateForToast } from '../utils/vibration';
import { normalizeUserFacingErrorMessage } from '../utils/errorMessages';
import { useInteractionPreferences } from '../contexts/InteractionPreferencesContext';
import { ToastContext } from '../contexts/ToastContext';

const ICONS = {
  success: <CheckCircle className="w-6 h-6 shrink-0 text-emerald-500" strokeWidth={2.2} />,
  error:   <XCircle    className="w-6 h-6 shrink-0 text-rose-500" strokeWidth={2.2} />,
  warning: <AlertTriangle className="w-6 h-6 shrink-0 text-amber-500" strokeWidth={2.2} />,
  info:    <Info       className="w-6 h-6 shrink-0 text-blue-500" strokeWidth={2.2} />,
};

const toastVariants = {
  initial: ({ animationsEnabled }) => (
    animationsEnabled
      ? { y: -60, opacity: 0, scale: 0.96 }
      : false
  ),
  animate: ({ animationsEnabled }) => ({
    y: 0,
    x: 0,
    opacity: 1,
    scale: 1,
    transition: animationsEnabled
      ? { type: 'spring', stiffness: 320, damping: 26 }
      : { duration: 0 }
  }),
  exit: ({ direction, animationsEnabled }) => {
    if (!animationsEnabled) return { opacity: 0 };
    const distance = typeof window !== 'undefined' ? Math.max(window.innerWidth, 600) : 600;

    if (direction === 'left') {
      return {
        x: -distance,
        opacity: 0,
        transition: { duration: 0.22, ease: 'easeOut' }
      };
    }
    if (direction === 'right') {
      return {
        x: distance,
        opacity: 0,
        transition: { duration: 0.22, ease: 'easeOut' }
      };
    }
    // Default exit (timeout or programmatic hide) -> slides up smoothly
    return {
      y: -60,
      opacity: 0,
      scale: 0.96,
      transition: { duration: 0.2, ease: 'easeIn' }
    };
  }
};

const Toast = ({
  toast,
  onDismiss,
  onPause,
  onResume,
  portalTarget = null,
  isGlobal = false
}) => {
  const toastContext = useContext(ToastContext);
  const { animationsEnabled } = useInteractionPreferences();
  const [exitDirection, setExitDirection] = useState('default');
  const toastId = toast?.id;
  const toastMessage = normalizeUserFacingErrorMessage(toast?.message, '');
  const toastType = toast?.type || 'info';
  const toastVibrationType = toast?.vibrationType || toastType;
  const toastActionLabel = String(toast?.actionLabel || '').trim();
  const toastOnAction = typeof toast?.onAction === 'function' ? toast.onAction : null;
  const isDraggingRef = useRef(false);

  // Reset exit direction to default whenever a new toast appears
  useEffect(() => {
    setExitDirection('default');
  }, [toastId]);

  useEffect(() => {
    if (toastId || toastMessage) {
      vibrateForToast(toastVibrationType);
    }
  }, [toastId, toastMessage, toastType, toastVibrationType]);

  // Synchronously prevent local duplicate renders when a global ToastProvider is active
  if (!isGlobal && toastContext && !portalTarget) {
    return null;
  }

  const target = portalTarget || (typeof document !== 'undefined' ? document.body : null);
  if (!target) return null;

  const handleDragEnd = (event, info) => {
    isDraggingRef.current = false;
    const { offset, velocity } = info;

    const isSwipeRight = offset.x > 60 || velocity.x > 250;
    const isSwipeLeft = offset.x < -60 || velocity.x < -250;

    if (isSwipeRight) {
      setExitDirection('right');
      onDismiss?.();
    } else if (isSwipeLeft) {
      setExitDirection('left');
      onDismiss?.();
    } else {
      onResume?.();
    }
  };

  const custom = {
    direction: exitDirection,
    animationsEnabled
  };

  return ReactDOM.createPortal(
    <AnimatePresence mode="wait" custom={custom}>
      {toast ? (
        <motion.div
          key={toast.id || 'toast-item'}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.8}
          onDragStart={() => {
            isDraggingRef.current = true;
            onPause?.();
          }}
          onDragEnd={handleDragEnd}
          onMouseEnter={onPause}
          onMouseLeave={onResume}
          onTouchStart={onPause}
          onTouchEnd={onResume}
          custom={custom}
          variants={toastVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="fixed left-3 right-3 top-3 z-[99999] cursor-grab active:cursor-grabbing sm:left-auto sm:right-6 sm:top-5 sm:w-auto sm:min-w-[320px] sm:max-w-md select-none touch-pan-y"
        >
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white/95 px-4 py-3 shadow-[0_4px_20px_rgba(0,0,0,0.15)] backdrop-blur-md transition-all dark:border-gray-700 dark:bg-gray-900/95 dark:shadow-[0_6px_24px_rgba(0,0,0,0.45)]">
            {ICONS[toastType] || ICONS.info}

            <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
              <span className="min-w-0 flex-1 whitespace-pre-line text-sm font-medium leading-snug text-gray-800 dark:text-gray-100 select-none">
                {toastMessage}
              </span>

              {toastActionLabel && toastOnAction ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss?.();
                    toastOnAction();
                  }}
                  className="shrink-0 rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/60"
                >
                  {toastActionLabel}
                </button>
              ) : null}
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    target
  );
};

export default Toast;
